import React, { useEffect, useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  Divider,
  DialogContent,
  Grid,
  DialogActions,
  Select,
  MenuItem,
} from '@mui/material';
import useSnackbar from '../../../hooks/useSnackbar';
import { CreateErrorCode, UpdateErrorCode } from '../../../services/brands';
import { ErrorCode } from '../../../utils/entities/interface';
import { Close } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from '../../atoms/Button';

interface FormData {
  id?: string;
  acType: string;
  models: string;
  code: string;
  solution: string;
  errorDescription: string;
  inverterType: 'INVERTOR' | 'NON_INVERTOR';
}

interface ErrorCodeAddEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  errorcode?: any;
  brandId: string;
}

export const AddEditErrorcodeModal = ({
  open,
  onClose,
  onSaved,
  errorcode,
  brandId,
}: ErrorCodeAddEditProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    acType: errorcode?.acType || '',
    models: errorcode?.models || '',
    code: errorcode?.code || '',
    solution: errorcode?.solution?.join(', ') || '',
    errorDescription: errorcode?.description || '',
    inverterType: errorcode?.category || 'INVERTOR',
  });
  // Use custom hook for Snackbar
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const isEditMode = Boolean(errorcode);

  useEffect(() => {
    if (errorcode) {
      setFormData({
        acType: errorcode?.acType || '',
        models: errorcode?.models || '',
        code: errorcode?.code || '',
        solution: errorcode?.solution?.join(', ') || '',
        errorDescription: errorcode?.description || '',
        inverterType: errorcode?.category || 'INVERTOR',
      });
    } else {
      handleReset();
    }
  }, [open]);

  console.log('errorcode', errorcode);
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        acType: formData.acType,
        models: formData.models,
        code: formData.code,
        brandId,
        description: formData.errorDescription,
        category: formData.inverterType,
        solution: formData?.solution
          ?.split(',')
          .map((item: any) => item.trim()),
      };

      const status =
        isEditMode && errorcode && errorcode?._id
          ? await UpdateErrorCode({
              errorCodeId: errorcode._id as string,
              acType: payload.acType,
              models: payload.models,
              code: payload.code,
              brandId: payload.brandId,
              solution: payload.solution,
              category: payload.category,
              description: payload.description,
            }) // Assume `UpdateErrorCode` is defined
          : await CreateErrorCode(payload);
      setIsLoading(false);
      if (status === 200) {
        showSnackbar(
          isEditMode
            ? 'Error code updated successfully!'
            : 'Error code created successfully!',
          'success',
        );
        handleReset();
        setTimeout(() => {
          onClose();
          if (onSaved) onSaved();
        }, 1500);
      } else {
        showSnackbar('Error: Failed to save error code', 'error');
      }
    } catch (error: any) {
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };

  const handleReset = () => {
    setFormData({
      acType: '',
      models: '',
      code: '',
      solution: '',
      errorDescription: '',
      inverterType: 'INVERTOR',
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        sx={{ borderRadius: 10, overflow: 'auto' }} // Ensuring rounded corners
        PaperProps={{
          sx: {
            borderRadius: 10,
            overflow: 'auto', // Ensures the background doesn't leak
          },
        }}
      >
        <Box
          sx={{
            border: '1px solid #7B1FA2',
            borderRadius: 10,
            overflow: 'auto',
          }}
        >
          {SnackbarComponent}
          <DialogTitle
            sx={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#181818',
              padding: '16px',
            }}
          >
            {isEditMode ? 'Edit Errorcode' : 'Add Errorcode'}
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography gutterBottom>AC Type</Typography>
                <TextField
                  name="acType"
                  value={formData.acType}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography gutterBottom>Models</Typography>
                <TextField
                  name="models"
                  value={formData.models}
                  onChange={handleChange}
                  fullWidth
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography gutterBottom>Error Code</Typography>
                <TextField
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  fullWidth
                  required
                  inputProps={{ maxLength: 10, pattern: '[0-9]*' }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography gutterBottom> Inverter Type</Typography>

                <Select
                  name="inverterType"
                  value={formData.inverterType}
                  onChange={handleChange}
                  fullWidth
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                >
                  <MenuItem value="INVERTOR">INVERTER</MenuItem>
                  <MenuItem value="NON_INVERTOR">NON INVERTOR</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom> Error Description</Typography>
                <TextField
                  name="errorDescription"
                  value={formData.errorDescription}
                  onChange={handleChange}
                  multiline
                  minRows={4}
                  fullWidth
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom>
                  {' '}
                  Troubleshooting (comma-separated)
                </Typography>
                <TextField
                  name="solution"
                  value={formData.solution}
                  onChange={handleChange}
                  multiline
                  minRows={4}
                  fullWidth
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
              <SecondaryButton title="Cancel" onClick={onClose} />
              <PrimaryButton title="Save" onClick={(e) => handleSubmit(e)} />
            </DialogActions>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};
